import React from "react";
import StarRating from "../StarRating";
import ReviewAuthorAvatar from "../ReviewAuthorAvatar";
import { formatReviewDateDe } from "../../utils/deletionCaseReviews";
import { normalizeReviewOutcome } from "../../utils/deletionCaseOutcomes";
import { formatEuro } from "../../utils/deletionCasePricing";
import { useTranslation } from "../../../i18n";
import DeletionCaseOutcomeBadge from "./DeletionCaseOutcomeBadge";

const WORD_TRUNCATE_LIMIT = 6;

function truncateReviewText(text, maxWords = WORD_TRUNCATE_LIMIT) {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return trimmed;
  return `${words.slice(0, maxWords).join(" ")}…`;
}

/**
 * @param {{
 *   review: object;
 *   index?: number;
 *   showReviewId?: boolean;
 * }} props
 */
const DeletionCaseReviewCard = ({
  review,
  index,
  showReviewId = true,
  showOutcomeBadge = false,
}) => {
  const { t } = useTranslation();
  const text = review.text || "";
  const previewText = truncateReviewText(text);
  const outcome = normalizeReviewOutcome(review.deletionOutcome);

  return (
    <article
      className="flex w-full flex-col rounded-[12px] border border-[#e8ecf0] bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_2px_8px_rgba(15,23,42,0.08)]"
      aria-label={t("deletionCaseUi.reviewAria", {
        id: index != null ? index + 1 : review.reviewId,
        author: review.author,
      })}
    >
      <div className="flex items-start gap-3">
        <ReviewAuthorAvatar author={review.author} photoUrl={review.authorPhoto} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-[#0f172a]">
            {review.author}
          </p>
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
            {showOutcomeBadge ? <DeletionCaseOutcomeBadge outcome={outcome} /> : null}
            <div className="flex flex-wrap items-center gap-2">
              <StarRating rating={review.rating} size="sm" />
              {review.unitPrice != null || review.lineTotal != null ? (
                <span
                  className={`rounded-md px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                    review.isFree
                      ? "bg-emerald-50 text-emerald-800"
                      : "bg-[#f0fafa] text-[#006666]"
                  }`}
                >
                  {review.isFree
                    ? t("deletionCaseUi.included")
                    : formatEuro(review.lineTotal ?? review.unitPrice)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-[#64748b]">
            {review.createTime ? (
              <time dateTime={review.createTime}>
                {formatReviewDateDe(review.createTime)}
              </time>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-3 flex-1">
        {text ? (
          <p className="text-[13px] leading-relaxed text-[#334155]" title={text}>
            {previewText}
          </p>
        ) : (
          <p className="text-[13px] italic text-[#94a3b8]">
            {t("deletionCaseUi.noReviewText")}
          </p>
        )}
      </div>

      {showReviewId ? (
        <footer className="mt-3 border-t border-[#f1f5f9] pt-3">
          <span className="rounded-md bg-[#f8fafc] px-2 py-1 text-[11px] font-medium tabular-nums text-[#64748b]">
            {t("deletionCaseUi.reviewNumber", { id: review.reviewId })}
          </span>
        </footer>
      ) : null}
    </article>
  );
};

export default DeletionCaseReviewCard;
